import React, { useCallback, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ChatPaneContainer,
  ChatPaneContent,
  ChatPaneGreeting,
  ChatPaneIcon,
  ChatPaneButtons,
  ChatPaneButton,
  ChatPaneInputWrapper,
  ChatPaneInputContainer,
  ChatPaneInput,
  ChatPaneInputActions,
  CancelButton,
  IconButton,
  ChatMessagesContainer,
  ChatMessage,
  UserMessage,
  AIMessage,
  MessageParagraph,
  LoaderMessage,
  ChatOptionsContainer,
  SmartAssistantLabel,
  RelatedFileText,
  RelatedFileLink,
  FactBox,
  FactContent,
  FactPageNumber,
  InlineCitationButton,
  CollapseIcon,
} from "./ChatPaneStyled";
import { Sparkle } from "@phosphor-icons/react";
import { AIAssistantChatIcon, NewChatIcon, SendChatMessageIcon, AIAssistantIconLoading } from "../../assets/icons";
import collapseIcon from "../../assets/images/Collapse.png";
import { chatBot, postHighlightedMatter } from "../../slice/MatterDetailsSlice";
import { getUserId } from "../../utils/fetchLocalStorage";
import { navigateToDocument } from "../../utils/navigationUrl";
import { getAIPaneTab } from "../../utils/aiPaneUrl";
import ChatMarkdown from "./ChatMarkdown";
import useDocumentAiReadiness from "../../hooks/useDocumentAiReadiness";
import DocumentChatGate, { ChatScopeToggle } from "../AIPane/DocumentChatGate";
import CollectionChatGate, {
  CollectionPartialNotice,
} from "../AIPane/CollectionChatGate";
import useCollectionAiReadiness, {
  isCollectionChattable,
} from "../../hooks/useCollectionAiReadiness";
import { fetchMatterCollections } from "../../service/documentCollections";
import {
  fetchCurrentChatThread,
  startNewChatThread,
  streamChatBot,
} from "../../service/matter";
import { probeChatFeedbackAccess } from "../../service/chatFeedback";
import ChatFeedbackControls from "./ChatFeedbackControls";

const ChatPane = ({ onClose, clearFactSelectionRef, setActiveTabRef, documentId }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const matterId = location.pathname.split("/matter/")[1]?.split("/")[0];
  const chatBotResponse = useSelector((state) => state.matterDetails.chatBotResponse);
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  // Live retrieval status from the agentic engine ("Reading a source
  // document…"), shown in the loader row while the model works.
  const [progressText, setProgressText] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [expandedCitations, setExpandedCitations] = useState(new Set());
  const [selectedFact, setSelectedFact] = useState(null); // Track selected fact: {messageId, citationIndex, factIndex}
  const [selectedFactDocumentId, setSelectedFactDocumentId] = useState(null); // Document id for the selected fact (to clear selection when that doc is closed)
  const [clickedOptionsMessages, setClickedOptionsMessages] = useState(new Set()); // Track messages whose options have been clicked
  const messagesEndRef = useRef(null);
  const lastProcessedTimestamp = useRef(null);
  const loadingStartTime = useRef(null);
  const hasCheckedOnMount = useRef(false);
  const processedResponseIds = useRef(new Set());
  const conversationIdCounter = useRef(0);
  const currentSessionId = useRef(null); // Track the current active session
  const currentRequestTimestamp = useRef(null); // Track the timestamp of the current request we're waiting for
  const streamAbortRef = useRef(null);
  const [threadHydrating, setThreadHydrating] = useState(true);

  // ── Chat-answer feedback (companion to dms_backend PR #1975) ──────
  // Thumbs on every assistant answer. Visibility follows the Brief
  // bar's 403-hide contract: one probe of the feedback list endpoint
  // per session (cached in the service module) decides whether the
  // buttons render at all; a 403 on a later POST revokes for the
  // session via onForbidden.
  const [feedbackAllowed, setFeedbackAllowed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    probeChatFeedbackAccess().then((allowed) => {
      if (!cancelled) setFeedbackAllowed(Boolean(allowed));
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Document-scoped chat (viewer surface) ──────────────────────────
  // When the pane is mounted with an active document, chat can scope
  // retrieval to that single PDF. Readiness (extracted + indexed) is
  // tracked per document; when the document isn't ready yet the
  // conversation area is replaced by the DocumentChatGate ("Prepare
  // for AI chat" CTA → progress → ready). The scope toggle lets the
  // user flip back to matter-wide chat at any time — that path is the
  // pre-existing behavior and never blocks on readiness.
  //
  // The scope the questions are asked under, and the document that scope
  // points at. `scopedDocumentId` is pinned: it is set at mount from
  // whatever document the panel opened over, and afterwards ONLY by the
  // user picking "This document". Opening or switching documents in the
  // viewer deliberately leaves both alone.
  //
  // This reverses the per-document reset added in PR #234, which flipped
  // the scope back to "document" and wiped the thread + session on every
  // document switch. Matthew, 2026-08-03: the panel has to stay put —
  // navigating the file must not disturb an open conversation. The
  // scope the user chose is the scope they keep until they change it.
  const [chatScope, setChatScope] = useState(() =>
    documentId ? "document" : "matter"
  );
  const [scopedDocumentId, setScopedDocumentId] = useState(
    () => documentId || null
  );
  // Picking "This document" is what (re)points document scope at the
  // document currently in the viewer.
  const handleScopeChange = useCallback(
    (nextScope) => {
      if (nextScope === "document") {
        setScopedDocumentId(documentId || null);
      }
      setChatScope(nextScope);
    },
    [documentId]
  );
  const {
    readiness,
    loading: readinessLoading,
    preparing,
    prepareError,
    prepare,
    refresh: refreshReadiness,
  } = useDocumentAiReadiness({ documentId: scopedDocumentId });
  // ── Collection-scoped chat ─────────────────────────────────────────
  // A saved collection ("her post-service medical records") is asked as
  // one unit. The backend resolves the collection to its PREPARED
  // members on every turn, so the pane only has to send the id.
  const [collections, setCollections] = useState([]);
  const [collectionId, setCollectionId] = useState(null);
  useEffect(() => {
    if (!matterId) return undefined;
    let cancelled = false;
    fetchMatterCollections(matterId)
      .then((data) => {
        if (!cancelled) setCollections(data?.collections || []);
      })
      .catch(() => {
        // No collections listed just means the scope option is absent;
        // document and matter chat are unaffected.
        if (!cancelled) setCollections([]);
      });
    return () => {
      cancelled = true;
    };
  }, [matterId]);

  const collectionScopeActive =
    chatScope === "collection" && Boolean(collectionId);
  const activeCollectionName =
    collections.find((item) => String(item.id) === String(collectionId))
      ?.name || "";
  const {
    readiness: collectionReadiness,
    loading: collectionLoading,
    preparing: collectionPreparing,
    prepareError: collectionPrepareError,
    prepare: prepareCollectionForChat,
    refresh: refreshCollectionReadiness,
  } = useCollectionAiReadiness({
    collectionId: collectionScopeActive ? collectionId : null,
  });

  const docScopeActive =
    Boolean(scopedDocumentId) && chatScope === "document";
  const docReady = readiness?.state === "ready";
  // Everything short of a confirmed-ready document blocks the composer
  // in document scope — including the readiness fetch itself and a
  // failed fetch (readiness null). The gate renders matching faces.
  const showDocumentGate = docScopeActive && !docReady;
  // A group opens as soon as ONE member is conversable: twelve scans
  // finish at twelve different times, and waiting for the slowest before
  // allowing any question would be the wrong trade. The partial notice
  // above the composer says how many are actually being searched.
  const collectionChattable = isCollectionChattable(
    collectionReadiness?.state
  );
  const showCollectionGate = collectionScopeActive && !collectionChattable;
  const showScopeGate = showDocumentGate || showCollectionGate;
  // Attached to every chatbot payload; the agent proxy forwards the body
  // as-is, and dms_ai ignores an absent scope. Collection scope wins over
  // document scope so the two can never both be sent.
  const documentScopePayload = collectionScopeActive
    ? collectionChattable
      ? { collection_id: String(collectionId) }
      : {}
    : docScopeActive && docReady
      ? { document_id: String(scopedDocumentId) }
      : {};

  // Live turns never carry their backend MatterChatMessage id: the SSE
  // proxy passes the agent's `done` frame through untouched and the JSON
  // path returns the agent response as-is, while `persist_turn` writes
  // the rows server-side only after the turn ends. So after a live turn
  // completes we refetch the current thread ONCE and stamp the newest
  // assistant row's id onto the answer we just rendered — that unlocks
  // the feedback thumbs for live answers, not just hydrated history.
  // The delay gives server-side persistence (which runs after the stream
  // drains, possibly after our early abort on the terminal frame) a beat
  // to land. If the refetch misses, the answer simply keeps no feedback
  // affordance until the next hydration.
  const stampBackendMessageId = useCallback(
    (localId) => {
      // No feedback capability → the id would never be used; skip the
      // refetch entirely so the feature costs nothing for everyone else.
      if (!feedbackAllowed || !matterId) return;
      const threadCollectionId =
        chatScope === "collection" && collectionId
          ? String(collectionId)
          : null;
      const threadDocumentId =
        !threadCollectionId && chatScope === "document" && scopedDocumentId
          ? String(scopedDocumentId)
          : null;
      setTimeout(() => {
        fetchCurrentChatThread({
          matterId,
          documentId: threadDocumentId,
          collectionId: threadCollectionId,
        })
          .then((response) => {
            const rows = response?.data?.messages || [];
            const lastAssistant = [...rows]
              .reverse()
              .find((row) => row.role === "assistant");
            if (lastAssistant?.id == null) return;
            setConversation((prev) => {
              // Already claimed by an earlier message (stale refetch or
              // a welcome row) — don't misattribute feedback.
              if (
                prev.some(
                  (m) => m.backendMessageId === lastAssistant.id
                )
              ) {
                return prev;
              }
              return prev.map((m) =>
                m.id === localId
                  ? { ...m, backendMessageId: lastAssistant.id }
                  : m
              );
            });
          })
          .catch(() => {
            // Feedback stays unavailable for this one answer.
          });
      }, 1200);
    },
    [chatScope, collectionId, feedbackAllowed, matterId, scopedDocumentId]
  );

  // Server-side thread hydration is the source of truth. Nothing containing
  // PHI is written to localStorage; scope changes load their own active
  // thread and New Chat archives it server-side.
  //
  // Note the dependency list: it tracks the PINNED scope, never the
  // viewer's `documentId`. Opening or switching documents must not blank
  // the pane and refetch a different thread underneath the user.
  useEffect(() => {
    if (!matterId) return undefined;
    let cancelled = false;
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    const threadCollectionId =
      chatScope === "collection" && collectionId ? String(collectionId) : null;
    const threadDocumentId =
      !threadCollectionId && chatScope === "document" && scopedDocumentId
        ? String(scopedDocumentId)
        : null;
    const scopePayload = threadCollectionId
      ? { collection_id: threadCollectionId }
      : threadDocumentId
        ? { document_id: threadDocumentId }
        : {};
    setThreadHydrating(true);
    setConversation([]);
    currentSessionId.current = null;

    fetchCurrentChatThread({
      matterId,
      documentId: threadDocumentId,
      collectionId: threadCollectionId,
    })
      .then((response) => {
        if (cancelled) return;
        const thread = response?.data || {};
        currentSessionId.current = thread.session_id || null;
        const hydrated = (thread.messages || []).map((item) => ({
          id: `persisted-${item.id}`,
          type: item.role === "assistant" ? "ai" : "user",
          message:
            item.message?.length > 0
              ? item.message
              : [{ text: item.content || "" }],
          options: item.options || [],
          failed: Boolean(item.failed),
          // MatterChatMessage pk — what the feedback endpoint anchors
          // to. Only assistant answers take feedback.
          backendMessageId: item.role === "assistant" ? item.id : null,
        }));
        setConversation(hydrated);
        if (hydrated.length === 0) {
          const userId = getUserId();
          if (userId) {
            const timestamp = new Date().toISOString();
            setIsLoading(true);
            loadingStartTime.current = new Date(timestamp);
            currentRequestTimestamp.current = timestamp;
            dispatch(
              chatBot({
                user_id: String(userId),
                session_id: thread.session_id || "",
                user_message: null,
                option_selected: "",
                matter_id: matterId,
                timestamp,
                ...scopePayload,
              })
            )
              .unwrap()
              .catch(() => {
                setIsLoading(false);
                loadingStartTime.current = null;
                currentRequestTimestamp.current = null;
              });
          }
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Failed to hydrate chat thread", error);
        // Degrade to a fresh ephemeral session instead of leaving the
        // empty-state spinner waiting forever on a hydration request that
        // already failed.
        const userId = getUserId();
        if (userId) {
          const timestamp = new Date().toISOString();
          setIsLoading(true);
          loadingStartTime.current = new Date(timestamp);
          currentRequestTimestamp.current = timestamp;
          dispatch(
            chatBot({
              user_id: String(userId),
              session_id: "",
              user_message: null,
              option_selected: "",
              matter_id: matterId,
              timestamp,
              ...scopePayload,
            })
          )
            .unwrap()
            .catch(() => {
              setIsLoading(false);
              loadingStartTime.current = null;
              currentRequestTimestamp.current = null;
            });
        }
      })
      .finally(() => {
        if (!cancelled) setThreadHydrating(false);
      });
    return () => {
      cancelled = true;
      streamAbortRef.current?.abort();
    };
  }, [chatScope, collectionId, dispatch, matterId, scopedDocumentId]);

  // Scroll to bottom when new messages are added.
  //
  // Scroll ONLY the messages container — never scrollIntoView. That call
  // scrolls every scrollable ancestor needed to reveal the target, and
  // when ChatPane auto-mounts (CAVC chat-only panel on matter load) it
  // yanked the whole app shell upward: the document tab strip and top
  // chrome went off-screen because html/body sit at overflow:hidden with
  // a 95vh body, which scrollIntoView happily offsets anyway (production
  // report 2026-07-16, "screen shifted up / tabs cut off").
  const scrollToBottom = () => {
    const container = messagesEndRef.current?.parentElement;
    container?.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation, isLoading]);

  // On first render, if the AI panel landed on the Chat tab via a refresh
  // but the chatBot welcome thunk never primed a session, ask the parent
  // to close the panel. Under the new AI-panel chrome the parent's
  // `onClose` is a no-op for the chat tab (the panel header owns
  // closing), but we keep the check so the legacy ChatPane-as-panel
  // mount in older entry points still has a fallback.
  useEffect(() => {
    if (!hasCheckedOnMount.current) {
      hasCheckedOnMount.current = true;
      const isChatActive = getAIPaneTab(location.search) === "chat";

      if (isChatActive && (!chatBotResponse || chatBotResponse?.type !== "welcome_message")) {
        onClose();
      }
    }
  }, [location.search, chatBotResponse, onClose]);

  // Reset tracking when location changes (navigation) to prevent processing stale responses
  useEffect(() => {
    // Clear transient response guards, but retain the durable session ID.
    // Matter/document/scope changes run the hydration effect above; folder
    // and citation navigation within the same thread must not disable the
    // composer by erasing an otherwise valid restored session.
    processedResponseIds.current.clear();
    lastProcessedTimestamp.current = null;
    loadingStartTime.current = null;
    currentRequestTimestamp.current = null; // Clear current request timestamp
  }, [location.pathname]);

  // Clear fact selection whenever the Chat tab leaves the foreground (the
  // panel closes entirely OR the user switches to Briefing/Analysis) so
  // the next return to the Chat tab doesn't reopen the surface with a
  // stale citation highlighted.
  //
  // ChatPane stays mounted across tab switches now (AIPane keeps all
  // three tab bodies mounted to preserve conversation state), so this
  // effect could otherwise fire for *every* `location.search` change —
  // including pure-document navigation that doesn't touch the AI tab.
  // We track the previous AI tab in a ref and only clear when the
  // user actually leaves Chat. The ref initializes to a sentinel and
  // is seeded by the effect on first render, so we don't reparse
  // `location.search` on every render.
  const prevAITabRef = useRef(null);
  useEffect(() => {
    const currentTab = getAIPaneTab(location.search);
    const prevTab = prevAITabRef.current;
    prevAITabRef.current = currentTab;
    if (prevTab === "chat" && currentTab !== "chat") {
      setSelectedFact(null);
      setSelectedFactDocumentId(null);
    }
  }, [location.search]);

  // Expose clear-selection-when-document-closed to parent (MatterDetails → TabView onTabClosed)
  useEffect(() => {
    if (!clearFactSelectionRef) return;
    clearFactSelectionRef.current = (closedDocId) => {
      if (closedDocId != null && String(closedDocId) === String(selectedFactDocumentId)) {
        setSelectedFact(null);
        setSelectedFactDocumentId(null);
      }
    };
    return () => {
      clearFactSelectionRef.current = null;
    };
  }, [clearFactSelectionRef, selectedFactDocumentId]);

  // Note: We don't add welcome_message to conversation initially
  // It's only used for displaying the initial greeting and options

  // Handle welcome message responses (for new chat only - when we requested welcome, not when user clicked an option)
  // Only clear loader if this welcome_message arrived AFTER we sent the request (prevents old cached welcome from clearing loader on first option click)
  useEffect(() => {
    if (
      chatBotResponse?.type === "welcome_message" &&
      isLoading &&
      loadingStartTime.current &&
      chatBotResponse?.timestamp
    ) {
      const responseTime = new Date(chatBotResponse.timestamp).getTime();
      const requestTime = loadingStartTime.current.getTime();
      // Only treat as response to current request if it arrived after we sent the request (not an old cached welcome)
      if (responseTime >= requestTime && responseTime - requestTime < 120000) {
        if (chatBotResponse?.session_id) {
          currentSessionId.current = chatBotResponse.session_id;
        }
        setIsLoading(false);
        loadingStartTime.current = null;
        lastProcessedTimestamp.current = chatBotResponse.timestamp;
      }
    }
  }, [chatBotResponse, isLoading]);

  // Handle function result responses
  useEffect(() => {
    if (chatBotResponse?.type !== "function_result" || !chatBotResponse?.timestamp) {
      return;
    }
    
    // CRITICAL: Only process responses that match the current session
    // This prevents old chat responses from appearing in new chats
    if (currentSessionId.current && chatBotResponse?.session_id !== currentSessionId.current) {
      // This response is from a different session, ignore it
      return;
    }
    
    // CRITICAL: Only process responses that are for the CURRENT request
    // This prevents old responses from being processed when a new request is sent
    if (currentRequestTimestamp.current) {
      const responseTime = new Date(chatBotResponse.timestamp);
      const requestTime = new Date(currentRequestTimestamp.current);
      
      // Only process if response timestamp is AFTER or EQUAL to the request timestamp
      // This ensures we only process responses that came after (or at the same time as) we sent the current request
      // If response timestamp is before request timestamp, it's definitely an old response from a previous request
      if (responseTime < requestTime) {
        console.log('🚫 Ignoring old response - response timestamp is before current request timestamp', {
          responseTime: chatBotResponse.timestamp,
          requestTime: currentRequestTimestamp.current
        });
        return;
      }
    }
    
    // Don't process function_result if we have no conversation and are waiting for welcome message
    // This prevents old responses from previous chat from appearing when starting a new chat
    const hasConversation = conversation.length > 0;
    const isWaitingForWelcome = !chatBotResponse || chatBotResponse?.type !== "welcome_message";
    if (!hasConversation && isLoading && isWaitingForWelcome) {
      // We're in a new chat state, ignore function_result responses
      return;
    }
    
    // Create a unique response ID to prevent duplicate processing
    const responseId = `${chatBotResponse.timestamp}-${chatBotResponse.session_id}`;
    
    // Check if we've already processed this response
    if (processedResponseIds.current.has(responseId)) {
      return;
    }
    
    // Check if this is a duplicate timestamp
    if (chatBotResponse.timestamp === lastProcessedTimestamp.current) {
      return;
    }
    
    // Check if response is within valid time window (2 minutes)
    const responseTime = new Date(chatBotResponse.timestamp);
    const now = new Date();
    const timeDiff = Math.abs(now - responseTime);
    
    // Only process if response is recent (within 2 minutes)
    if (timeDiff > 120000) {
      return; // Response is too old, ignore it
    }
    
    // Process if:
    // 1. Currently loading AND loadingStartTime exists (normal case)
    // 2. OR not loading but response is recent AND we have an active conversation (late-arriving response)
    const isNormalCase = isLoading && loadingStartTime.current && 
      Math.abs(responseTime - loadingStartTime.current) < 120000;
    const isLateResponse = !isLoading && hasConversation && timeDiff < 30000; // Late response within 30 seconds, only if conversation exists
    
    if (isNormalCase || isLateResponse) {
      // Mark this response as processed
      processedResponseIds.current.add(responseId);
      lastProcessedTimestamp.current = chatBotResponse.timestamp;
      setIsLoading(false);
      loadingStartTime.current = null;
      // Clear current request timestamp after processing - we've received the response
      currentRequestTimestamp.current = null;
      
      // Generate unique ID for this conversation message
      const conversationId = `ai-${++conversationIdCounter.current}-${Date.now()}`;
      
      // Add the response to conversation
      // Note: We don't check for duplicate content here because:
      // 1. The responseId (timestamp + session_id) already prevents the same API response from being added twice
      // 2. Users should be able to click the same option again and get a new response, even if it has the same content
      setConversation((prev) => [
        ...prev,
        {
          id: conversationId,
          type: "ai",
          message: chatBotResponse.message,
          options: chatBotResponse.options || [],
        },
      ]);
      // The JSON path persists the turn server-side without returning
      // the row id; pick it up so this answer takes feedback too.
      stampBackendMessageId(conversationId);
    }
  }, [chatBotResponse, isLoading, conversation.length, stampBackendMessageId]);

  // Check if we're waiting for initial welcome message
  // A hydrated durable thread already has a usable session even if the
  // global Redux response contains a non-welcome result from another scope.
  const isWaitingForWelcome = !currentSessionId.current;
  
  // Get initial greeting and options from welcome_message
  const isWelcomeMessage = chatBotResponse?.type === "welcome_message";
  const greeting = isWelcomeMessage 
    ? chatBotResponse.message?.[0]?.text || ""
    : "";
  const initialOptions = isWelcomeMessage 
    ? chatBotResponse.options?.map((option) => option.label) || []
    : [];

  const handleNewChat = async () => {
    // Get user_id from localStorage
    const userId = getUserId();
    // Generate timestamp in ISO format
    const timestamp = new Date().toISOString();

    if (matterId && userId) {
      // Clear conversation history FIRST - do this immediately
      setConversation([]);
      // Clear clicked options state
      setClickedOptionsMessages(new Set());
      // Reset refs - clear all tracking to prevent stale responses
      lastProcessedTimestamp.current = null;
      processedResponseIds.current.clear();
      conversationIdCounter.current = 0;
      currentSessionId.current = null; // Clear current session - new chat will get new session
      loadingStartTime.current = null; // Clear before setting new one
      currentRequestTimestamp.current = null; // Clear current request timestamp
      // Show loader
      setIsLoading(true);
      loadingStartTime.current = new Date();
      currentRequestTimestamp.current = timestamp; // Track this request timestamp

      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      try {
        await startNewChatThread({
          matterId,
          documentId:
            chatScope === "document" && scopedDocumentId
              ? String(scopedDocumentId)
              : null,
          collectionId:
            chatScope === "collection" && collectionId
              ? String(collectionId)
              : null,
        });
        const payload = {
          user_id: String(userId),
          session_id: "",
          user_message: null,
          option_selected: "",
          matter_id: matterId,
          timestamp: timestamp,
          ...documentScopePayload,
        };
        dispatch(chatBot(payload)).unwrap().catch(() => clearLoaderState());
      } catch (_error) {
        clearLoaderState();
      }
    }
  };

  // Clear the AI loader + loading-state refs. Used by both error paths:
  // `handleNewChat` (no user bubble to mark — the welcome request is a
  // session-bootstrap, not a user message) AND `markMessageFailed`
  // below for `handleSendMessage` / `handleOptionClick`.
  const clearLoaderState = () => {
    setIsLoading(false);
    setProgressText("");
    loadingStartTime.current = null;
    currentRequestTimestamp.current = null;
  };

  const handleCancel = () => {
    // Abort the native fetch so a stalled SSE connection does not keep
    // the request alive after the composer has been released.
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
    clearLoaderState();
  };

  // Mark a previously-echoed user bubble as failed so the user sees their
  // message *plus* a small "Failed to send" affordance, instead of having
  // their message vanish silently. Also clears the AI loader since no
  // response is coming.
  const markMessageFailed = (conversationId) => {
    clearLoaderState();
    setConversation((prev) =>
      prev.map((msg) =>
        msg.id === conversationId ? { ...msg, failed: true } : msg
      )
    );
  };

  const handleOptionClick = (optionLabel, messageId) => {
    // Hide options for this message immediately
    setClickedOptionsMessages((prev) => new Set(prev).add(messageId));

    // Optimistically echo the user's choice into the conversation thread
    // BEFORE checking dispatch preconditions. Standard chat-app UX: the
    // user's bubble appears the instant they act, regardless of whether
    // the network round-trip succeeds. If preconditions fail (e.g.
    // session not yet established), we mark the bubble as failed below.
    const conversationId = `user-${++conversationIdCounter.current}-${Date.now()}`;
    setConversation((prev) => [
      ...prev,
      {
        id: conversationId,
        type: "user",
        message: [{ text: optionLabel }],
      },
    ]);

    // Extract matter_id from URL path
    const matterId = location.pathname.split("/matter/")[1]?.split("/")[0];
    // Get user_id from localStorage
    const userId = getUserId();
    // Get session_id from current session (from welcome message) or fallback to chatBotResponse
    const sessionId = currentSessionId.current || chatBotResponse?.session_id || "";
    // Generate latest timestamp
    const timestamp = new Date().toISOString();

    if (matterId && userId && sessionId) {
      setIsLoading(true);
      setProgressText("");
      loadingStartTime.current = new Date();
      currentRequestTimestamp.current = timestamp; // Track this request timestamp - CRITICAL for preventing old responses

      const payload = {
        user_id: String(userId),
        session_id: sessionId,
        user_message: null,
        option_selected: optionLabel,
        matter_id: matterId,
        timestamp: timestamp,
        ...documentScopePayload,
      };
      dispatch(chatBot(payload))
        .unwrap()
        .catch(() => markMessageFailed(conversationId));
    } else {
      markMessageFailed(conversationId);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    const messageText = inputValue.trim();
    if (!messageText) return;

    // Optimistically echo the user's message into the conversation thread
    // and clear the input field BEFORE checking dispatch preconditions.
    // Standard chat-app UX (Slack / iMessage / etc.): the user's bubble
    // appears in the thread the instant they hit send, the input clears,
    // and then the AI typing-indicator follows. If preconditions fail or
    // the dispatch rejects, the bubble is marked failed below so the
    // message never silently disappears.
    const conversationId = `user-${++conversationIdCounter.current}-${Date.now()}`;
    setConversation((prev) => [
      ...prev,
      {
        id: conversationId,
        type: "user",
        message: [{ text: messageText }],
      },
    ]);
    setInputValue("");

    // Get user_id from localStorage
    const userId = getUserId();
    // Get session_id from current session (from welcome message) or fallback to chatBotResponse
    const sessionId = currentSessionId.current || chatBotResponse?.session_id || "";
    // Generate latest timestamp
    const timestamp = new Date().toISOString();

    if (matterId && userId && sessionId) {
      setIsLoading(true);
      // A fresh turn must not inherit the previous turn's retrieval
      // status line (an aborted stream skips clearLoaderState).
      setProgressText("");
      loadingStartTime.current = new Date();
      currentRequestTimestamp.current = timestamp; // Track this request timestamp - CRITICAL for preventing old responses

      const payload = {
        user_id: String(userId),
        session_id: sessionId,
        user_message: messageText,
        option_selected: '',
        matter_id: matterId,
        timestamp: timestamp,
        client_message_id: conversationId,
        ...documentScopePayload,
      };
      const controller = new AbortController();
      streamAbortRef.current?.abort();
      streamAbortRef.current = controller;
      const streamMessageId = `stream-${conversationId}`;
      const isCurrentStream = () => streamAbortRef.current === controller;
      let accumulated = "";
      let receivedDelta = false;
      let finalMessages = null;
      // The turn can complete twice (terminal frame + stream close);
      // the id refetch must run once.
      let idStampScheduled = false;
      const scheduleIdStamp = () => {
        if (idStampScheduled) return;
        idStampScheduled = true;
        stampBackendMessageId(streamMessageId);
      };
      try {
        await streamChatBot(payload, {
          signal: controller.signal,
          onEvent: (event, data) => {
            if (!isCurrentStream()) return;
            if (data?.session_id) currentSessionId.current = data.session_id;
            if (event === "progress") {
              // The agentic engine is retrieving ("Reading a source
              // document…"). Show the status in the loader row.
              setProgressText(data.text || "");
              setIsLoading(true);
            }
            if (event === "answer_reset") {
              // Text streamed so far was working narration before a
              // tool call, not the answer. Clear it and keep waiting.
              accumulated = "";
              receivedDelta = false;
              setIsLoading(true);
              setConversation((prev) =>
                prev.filter((item) => item.id !== streamMessageId)
              );
            }
            if (event === "delta") {
              receivedDelta = true;
              setProgressText("");
              accumulated += data.text || "";
              // Hide complete and partial citation control tokens while
              // preserving the streamed prose around them.
              const visible = accumulated
                .replace(/\[\[C\d+\]\]/gi, "")
                .replace(/\[\[[^\]]*$/g, "")
                .trim();
              setIsLoading(false);
              setConversation((prev) => {
                const nextMessage = {
                  id: streamMessageId,
                  type: "ai",
                  message: [
                    {
                      id: 1,
                      message_type: "summary",
                      text: visible,
                    },
                  ],
                };
                const index = prev.findIndex(
                  (item) => item.id === streamMessageId
                );
                if (index === -1) return [...prev, nextMessage];
                const copy = [...prev];
                copy[index] = nextMessage;
                return copy;
              });
            }
            if (event === "citations" || event === "done") {
              const hasCompleteAnswer = Boolean(data.messages?.length || receivedDelta);
              if (data.messages?.length) {
                finalMessages = data.messages;
                setConversation((prev) => {
                  const nextMessage = {
                    id: streamMessageId,
                    type: "ai",
                    message: data.messages,
                  };
                  const index = prev.findIndex(
                    (item) => item.id === streamMessageId
                  );
                  if (index === -1) return [...prev, nextMessage];
                  const copy = [...prev];
                  copy[index] = nextMessage;
                  return copy;
                });
              }
              // Citations are the complete parsed answer. Do not wait for
              // the underlying fetch reader to close before releasing the
              // composer; a proxy can deliver the terminal frame while
              // keeping the connection open.
              if (hasCompleteAnswer) {
                controller.abort();
                streamAbortRef.current = null;
                clearLoaderState();
                scheduleIdStamp();
              }
            }
            if (event === "error") {
              throw new Error(data.detail || "Chat stream failed");
            }
          },
        });
        if (!isCurrentStream()) return;
        setIsLoading(false);
        loadingStartTime.current = null;
        currentRequestTimestamp.current = null;
        if (!finalMessages && !receivedDelta) {
          throw new Error("Chat stream ended without an answer");
        }
        // Natural stream close (no early abort on a terminal frame).
        scheduleIdStamp();
      } catch (error) {
        if (!isCurrentStream()) return;
        if (error?.name === "AbortError") {
          clearLoaderState();
          return;
        }
        if (!receivedDelta) {
          // Safe fallback only before any streamed answer was shown, so the
          // fallback request remains the active request until its Redux
          // response clears the loader.
          dispatch(chatBot(payload))
            .unwrap()
            .catch(() => markMessageFailed(conversationId));
        } else {
          clearLoaderState();
          markMessageFailed(conversationId);
        }
      } finally {
        if (streamAbortRef.current === controller) {
          streamAbortRef.current = null;
        }
      }
    } else {
      markMessageFailed(conversationId);
    }
  };

  const hasConversation = conversation.length > 0;

  const handleInlineCitationClick = (citation) => (event) => {
    event.preventDefault();
    if (!citation?.document_id || !matterId) return;
    const page = citation.start_page || citation.page;
    const chunkContent = (citation.chunk_content || "")
      .replaceAll(/\s+/g, " ")
      .replaceAll("# ", "")
      .trim();
    setSelectedFact(`inline-${citation.source_key}`);
    setSelectedFactDocumentId(citation.document_id);
    navigateToDocument(
      `/${matterId}/D${citation.document_id}`,
      dispatch,
      navigate,
      postHighlightedMatter,
      "pdf",
      true,
      {
        highlightMetadata: [
          {
            ...citation,
            page,
            chunk_content: chunkContent,
          },
        ],
      },
      setActiveTabRef
    );
  };

  // Helper function to render citations (extracted to reduce nesting)
  const renderCitations = (citations, msgId, messageId, location, dispatch, navigate, postHighlightedMatter, expandedCitations, setExpandedCitations, selectedFact, setSelectedFact, setSelectedFactDocumentId) => {
    return citations.map((citation, citationIndex) => {
      // Extract matter_id from URL path
      const matterId = location.pathname.split("/matter/")[1]?.split("/")[0];
      
      if (!citation.document_id || !matterId) {
        return null;
      }
      
      // Construct relative_path: /matterId/DdocumentId
      const relativePath = `/${matterId}/D${citation.document_id}`;
      const fileType = 'pdf';
      
      // Create unique key for this citation
      const citationKey = `${msgId}-${messageId}-${citationIndex}-${citation.document_id}`;
      const isExpanded = expandedCitations.has(citationKey);
      const factsCount = citation.offset_metadata?.length || 0;
      
      // Handler for document name click - navigate to document. Opens
      // at the first cited page (with its snippet highlighted) instead
      // of page 1, so "Related File" lands the reader on the passage
      // the answer used.
      const handleDocumentClick = (e) => {
        e.preventDefault();
        setSelectedFact(null);
        setSelectedFactDocumentId(null);
        const firstFact = citation.offset_metadata?.[0];
        let navigationState = null;
        if (firstFact) {
          const cleanedContent = (firstFact.chunk_content || "")
            .replaceAll(/\s+/g, " ")
            .replaceAll("# ", "")
            .trim();
          navigationState = {
            highlightMetadata: [
              {
                ...firstFact,
                page: firstFact.start_page || firstFact.page,
                chunk_content: cleanedContent,
              },
            ],
          };
        }
        try {
          navigateToDocument(
            relativePath,
            dispatch,
            navigate,
            postHighlightedMatter,
            fileType,
            true,
            navigationState,
            setActiveTabRef
          );
        } catch (error) {
          console.error("ChatPane: Error navigating to document", error);
        }
      };
      
      // Handler for fact clicks - each fact navigates with its specific offset_metadata
      const handleFactClick = (offsetMetadata, factIndex) => (e) => {
        let offsetMetadataCopy = structuredClone(offsetMetadata);
        console.log('🔍 Offset metadata:', offsetMetadataCopy);
        e.preventDefault();
        
        // Set this fact as selected
        const factKey = `${msgId}-${messageId}-${citationIndex}-${factIndex}`;
        setSelectedFact(factKey);
        setSelectedFactDocumentId(citation.document_id);
        
        let cleanedText = offsetMetadataCopy.chunk_content
          .replaceAll('\r\n', ' ')      // Windows line breaks
          .replaceAll('\n', ' ')         // Unix line breaks
          .replaceAll('\r', ' ')         // Mac line breaks
          .replaceAll('\t', ' ')         // Tabs
          .replaceAll('# ', '')          // Remove markdown headers (# )
          .replaceAll(/\s+/g, ' ')        // Multiple spaces to single space
          .trim();
        offsetMetadataCopy.chunk_content = cleanedText;
        console.log(`📝 Original chunk length: ${offsetMetadata.chunk_content.length}, Cleaned length: ${cleanedText.length}`);
        console.log(`📝 First 100 chars of cleaned text:`, cleanedText.substring(0, 100));
    
        // Pass only this specific offset_metadata for highlighting
        const navigationState = {
          highlightMetadata: [offsetMetadataCopy]
        };
        
        try {
          navigateToDocument(
            relativePath,
            dispatch,
            navigate,
            postHighlightedMatter,
            fileType,
            true,
            navigationState,
            setActiveTabRef
          );
        } catch (error) {
          console.error("ChatPane: Error navigating to document", error);
        }
      };
      
      // Toggle expand/collapse
      const toggleExpand = (e) => {
        e.preventDefault();
        setExpandedCitations((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(citationKey)) {
            newSet.delete(citationKey);
          } else {
            newSet.add(citationKey);
          }
          return newSet;
        });
      };
      
      // Truncate chunk content for display
      const truncateContent = (content, maxLength = 80) => {
        if (!content) return '';
        if (content.length <= maxLength) return content;
        return content.substring(0, maxLength) + '...';
      };
      
      return (
        <div key={`citation-${messageId}-${citationIndex}`} style={{ marginBottom: '16px' }}>
          {/* Related File with clickable document name */}
          <RelatedFileText style={{ marginBottom: '8px' }}>
            Related File :{" "}
            <RelatedFileLink 
              href="#" 
              onClick={handleDocumentClick}
            >
              {citation.document_name}
            </RelatedFileLink>
          </RelatedFileText>
          
          {/* Show/Hide facts toggle */}
          {factsCount > 0 && (
            <div style={{ marginBottom: '8px' }}>
              <RelatedFileLink 
                href="#" 
                onClick={toggleExpand}
                style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                {isExpanded ? (
                  <>
                    Hide facts ({factsCount})
                    <CollapseIcon src={collapseIcon} alt="Collapse" open={isExpanded} />
                  </>
                ) : (
                  <>
                    Show facts ({factsCount})
                    <CollapseIcon src={collapseIcon} alt="Collapse" open={isExpanded} />
                  </>
                )}
              </RelatedFileLink>
            </div>
          )}
          
          {/* Expanded facts list */}
          {isExpanded && citation.offset_metadata && citation.offset_metadata.length > 0 && (
            <div style={{ marginTop: '8px' }}>
              {citation.offset_metadata.map((offsetData, factIndex) => {
                const factKey = `${msgId}-${messageId}-${citationIndex}-${factIndex}`;
                const isSelected = selectedFact === factKey;
                
                return (
                  <FactBox
                    key={`fact-${messageId}-${citationIndex}-${factIndex}`}
                    onClick={handleFactClick(offsetData, factIndex)}
                    isSelected={isSelected}
                  >
                    <FactContent>
                      {truncateContent(offsetData.chunk_content)}
                    </FactContent>
                    <FactPageNumber isSelected={isSelected}>
                      Page {offsetData.page}
                    </FactPageNumber>
                  </FactBox>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <ChatPaneContainer>
      {/* The old "AI Assistant" header row (New Chat / title / X) was
          removed 2026-07-16 (Matthew): ChatPane only renders inside the
          AI panel now, which owns the header and close affordance — the
          inner X was a no-op there and the row was dead chrome. New
          Chat moved next to the input (see ChatPaneInputActions). */}
      <ChatPaneContent hasConversation={hasConversation && !showScopeGate}>
        {showCollectionGate ? (
          <CollectionChatGate
            collectionName={activeCollectionName}
            readiness={collectionReadiness}
            loading={collectionLoading}
            preparing={collectionPreparing}
            prepareError={collectionPrepareError}
            onPrepare={prepareCollectionForChat}
            onRetryStatus={refreshCollectionReadiness}
          />
        ) : showDocumentGate ? (
          <DocumentChatGate
            readiness={readiness}
            loading={readinessLoading}
            preparing={preparing}
            prepareError={prepareError}
            onPrepare={prepare}
            onRetryStatus={refreshReadiness}
          />
        ) : hasConversation ? (
          <ChatMessagesContainer>
            {conversation.map((msg) => {
              // Use the unique ID assigned when message was added to conversation
              const msgKey = msg.id || `msg-${msg.type}-${Date.now()}-${Math.random()}`;
              
              return (
              <React.Fragment key={msgKey}>
                {msg.type === "user" && (
                  <ChatMessage>
                    <UserMessage>{msg.message[0]?.text}</UserMessage>
                    {msg.failed && (
                      <span
                        role="status"
                        style={{
                          alignSelf: "flex-end",
                          color: "#B42318",
                          fontSize: "12px",
                          marginTop: "4px",
                        }}
                      >
                        Failed to send
                      </span>
                    )}
                  </ChatMessage>
                )}
                {msg.type === "ai" && (
                  <ChatMessage>
                    <SmartAssistantLabel>
                      <Sparkle size={14} weight="fill" />
                      <span>Smart Assistant</span>
                    </SmartAssistantLabel>
                    <AIMessage>
                      {(() => {
                        // Group messages by type to handle document lists properly
                        const summaryMessages = msg.message.filter(m => m.message_type === "summary");
                        const documentMessages = msg.message.filter(m => m.message_type === "document");
                        const otherMessages = msg.message.filter(m => !m.message_type || (m.message_type !== "summary" && m.message_type !== "document"));
                        
                        return (
                          <>
                          {otherMessages.map((m) => (
                              <MessageParagraph key={`other-${m.id}`}>{m.text}</MessageParagraph>
                            ))}
                            {summaryMessages.map((m) => (
                              <div key={`summary-${m.id}`}>
                                {/* as="div": markdown answers contain
                                    block elements (lists, tables) that
                                    are invalid inside a <p>. */}
                                <MessageParagraph as="div">
                                  {(m.segments?.length
                                    ? m.segments
                                    : [
                                        {
                                          text: m.text,
                                          inline_citations:
                                            m.inline_citations || [],
                                        },
                                      ]
                                  ).map((segment, segmentIndex) => (
                                    <React.Fragment
                                      key={`${m.id}-segment-${segmentIndex}`}
                                    >
                                      <ChatMarkdown text={segment.text} />
                                      {segment.inline_citations?.map(
                                        (citation) => {
                                          const start =
                                            citation.start_page ||
                                            citation.page;
                                          const end =
                                            citation.end_page || start;
                                          const label =
                                            end > start
                                              ? `pp. ${start}–${end}`
                                              : `p. ${start}`;
                                          return (
                                            <InlineCitationButton
                                              type="button"
                                              key={`${m.id}-${segmentIndex}-${citation.source_key}`}
                                              onClick={handleInlineCitationClick(
                                                citation
                                              )}
                                              title={`Open ${citation.document_name}, ${label}`}
                                            >
                                              {label}
                                            </InlineCitationButton>
                                          );
                                        }
                                      )}
                                    </React.Fragment>
                                  ))}
                                </MessageParagraph>
                              </div>
                            ))}
                            {documentMessages.map((m) => {
                              return (
                              <div key={`document-${m.id}`}>
                                {m.citations && m.citations.length > 0 && (
                                  <>
                                    {                                    renderCitations(
                                      m.citations,
                                      msg.id,
                                      m.id,
                                      location,
                                      dispatch,
                                      navigate,
                                      postHighlightedMatter,
                                      expandedCitations,
                                      setExpandedCitations,
                                      selectedFact,
                                      setSelectedFact,
                                      setSelectedFactDocumentId
                                    )}
                                  </>
                                )}
                              </div>
                            );
                            })}
                            
                          </>
                        );
                      })()}
                    </AIMessage>
                    {feedbackAllowed && msg.backendMessageId != null && (
                      <ChatFeedbackControls
                        messageId={msg.backendMessageId}
                        onForbidden={() => setFeedbackAllowed(false)}
                      />
                    )}
                    {msg.options && msg.options.length > 0 && !clickedOptionsMessages.has(msg.id) && (
                      <ChatOptionsContainer>
                        {msg.options.map((option) => (
                          <ChatPaneButton
                            key={option.id || option.label}
                            onClick={() => handleOptionClick(option.label, msg.id)}
                          >
                            {option.label}
                          </ChatPaneButton>
                        ))}
                      </ChatOptionsContainer>
                    )}
                  </ChatMessage>
                )}
              </React.Fragment>
              );
            })}
            {isLoading && (
              <ChatMessage>
                <LoaderMessage>
                  <AIAssistantIconLoading />
                  <span style={{ marginLeft: '8px' }}>
                    {progressText || "I'm looking that up for you now..."}
                  </span>
                </LoaderMessage>
              </ChatMessage>
            )}
            <div ref={messagesEndRef} />
          </ChatMessagesContainer>
        ) : (
          <>
            {threadHydrating || isLoading || isWaitingForWelcome ? (
              <ChatMessagesContainer>
                <ChatMessage>
                  <LoaderMessage>
                    <AIAssistantIconLoading />
                    <span style={{ marginLeft: '8px' }}>I&apos;m looking that up for you now...</span>
                  </LoaderMessage>
                </ChatMessage>
                <div ref={messagesEndRef} />
              </ChatMessagesContainer>
            ) : (
              <>
                <ChatPaneIcon>
                  <AIAssistantChatIcon />
                </ChatPaneIcon>
                {greeting && <ChatPaneGreeting>{greeting}</ChatPaneGreeting>}
                {initialOptions.length > 0 && !clickedOptionsMessages.has("welcome") && (
                  <ChatPaneButtons>
                    {initialOptions.map((prompt) => (
                      <ChatPaneButton key={prompt} onClick={() => handleOptionClick(prompt, "welcome")}>
                        {prompt}
                      </ChatPaneButton>
                    ))}
                  </ChatPaneButtons>
                )}
              </>
            )}
          </>
        )}
      </ChatPaneContent>

      <ChatPaneInputWrapper>
        <CollectionPartialNotice
          readiness={collectionScopeActive ? collectionReadiness : null}
        />
        {(documentId || scopedDocumentId || collections.length > 0) && (
          <ChatScopeToggle
            scope={chatScope}
            onChange={handleScopeChange}
            documentId={documentId || scopedDocumentId}
            collections={collections}
            collectionId={collectionId}
            onCollectionChange={setCollectionId}
          />
        )}
        <ChatPaneInputContainer as="form" onSubmit={handleSendMessage}>
          <ChatPaneInputActions>
            {/* New Chat lives by the input since the header row was
                removed. Disabled behind the readiness gate: a New Chat
                there would bootstrap a matter-wide session under a
                document or collection scope. */}
            <IconButton
              type="button"
              aria-label="New Chat"
              title="New Chat"
              onClick={handleNewChat}
              disabled={threadHydrating || isLoading || showScopeGate}
            >
              <NewChatIcon />
            </IconButton>
          </ChatPaneInputActions>
          <ChatPaneInput
            type="text"
            placeholder={
              collectionScopeActive && collectionChattable
                ? `Ask across ${activeCollectionName || "this collection"}`
                : docScopeActive && docReady
                  ? "Ask about this document"
                  : "Ask AI Assistant"
            }
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={
              threadHydrating ||
              isWaitingForWelcome ||
              isLoading ||
              showScopeGate
            }
          />
          <ChatPaneInputActions>
            {isLoading && streamAbortRef.current && (
              <CancelButton
                type="button"
                aria-label="Cancel"
                onClick={handleCancel}
              >
                Cancel
              </CancelButton>
            )}
            <IconButton
              aria-label="Send"
              type="submit"
              disabled={
                threadHydrating ||
                isWaitingForWelcome ||
                isLoading ||
                showScopeGate ||
                !inputValue.trim()
              }
            >
              <SendChatMessageIcon size={20} />
            </IconButton>
          </ChatPaneInputActions>
        </ChatPaneInputContainer>
      </ChatPaneInputWrapper>
    </ChatPaneContainer>
  );
};

ChatPane.propTypes = {
  onClose: PropTypes.func.isRequired,
  clearFactSelectionRef: PropTypes.shape({ current: PropTypes.any }),
  setActiveTabRef: PropTypes.shape({ current: PropTypes.func }),
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ChatPane;
